import React, { useCallback } from 'react';
import cx from 'classnames';
import { Cell } from '@tanstack/react-table';
import { PlacesType } from 'react-tooltip';
import { useTooltipGroupContext } from '@/components/shared/tooltip/TooltipGroup';
import styles from '../Table.module.scss';

type TableCellContentProps<T, TValue> = {
  cell: Cell<T, TValue>;
  children: React.ReactNode;
  tooltipPlace?: PlacesType;
};

type TableCellContentColumnDef<T, TValue> = Cell<T, TValue>['column']['columnDef'] & {
  additionalContentCellClassname?: string;
  tooltipPlace?: PlacesType;
};

const isNonTextElement = (element: Element): boolean =>
  element instanceof SVGElement ||
  element instanceof HTMLMediaElement ||
  element instanceof HTMLImageElement ||
  element instanceof HTMLInputElement ||
  element instanceof HTMLSelectElement ||
  element instanceof HTMLTextAreaElement ||
  element instanceof HTMLButtonElement ||
  element instanceof HTMLCanvasElement ||
  element instanceof HTMLIFrameElement ||
  element instanceof HTMLObjectElement ||
  element instanceof HTMLEmbedElement ||
  element instanceof HTMLPictureElement ||
  element.getAttribute('role') === 'img' ||
  element.getAttribute('role') === 'button';

const isTextOnlyContent = (element: HTMLElement): boolean =>
  !Array.from(element.querySelectorAll('*')).some(isNonTextElement);

const hasOwnTooltip = (element: HTMLElement): boolean => !!element.querySelector('[data-tooltip-id]');

const isCellTruncated = (element: HTMLElement): boolean =>
  element.scrollWidth > element.clientWidth ||
  Array.from(element.querySelectorAll('*')).some(
    child => child instanceof HTMLElement && child.scrollWidth > child.clientWidth,
  );

const TableCellContent = <T, TValue>({ cell, children, tooltipPlace = 'top' }: TableCellContentProps<T, TValue>) => {
  const columnDef: TableCellContentColumnDef<T, TValue> = cell.column.columnDef;
  const resolvedTooltipPlace = columnDef.tooltipPlace ?? tooltipPlace;
  const combinedContentCellClassName = cx(styles.contentCell, columnDef.additionalContentCellClassname);
  const tooltipContext = useTooltipGroupContext();

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const cellElement = e.currentTarget;
    if (hasOwnTooltip(cellElement)) return;
    if (!isTextOnlyContent(cellElement)) return;

    const textContent = cellElement.textContent?.trim();
    if (!textContent || !isCellTruncated(cellElement)) return;

    cellElement.setAttribute('data-tooltip-content', textContent);
    cellElement.setAttribute('data-tooltip-hidden', 'false');
  }, []);

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.setAttribute('data-tooltip-hidden', 'true');
    e.currentTarget.removeAttribute('data-tooltip-content');
  }, []);

  return (
    <div
      className={combinedContentCellClassName}
      data-tooltip-id={tooltipContext?.id}
      data-tooltip-hidden={true}
      data-tooltip-place={resolvedTooltipPlace}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
};

export default TableCellContent;